import { motion } from 'framer-motion'
import { Link } from 'gatsby'
import React, { useRef } from 'react'
import { useLatestFeaturedArticle } from '../../hooks/useLatestFeaturedArticle'
import { useParallaxCalculation } from '../../hooks/useParallax'
import { usePathPrefix } from '../../hooks/usePathPrefix'
import { useReadTime } from '../../hooks/useReadTime'
import { useTranslations } from '../../hooks/useTranslations'

const MagazineHeading = props => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="174"
    height="67"
    fill="none"
    viewBox="0 0 174 67"
    {...props}
  >
    <path
      stroke="#000"
      d="M164.167 55.04c0 6.42-5.27 10.958-11.759 10.958-6.576 0-10.844-3.553-10.409-10.317h6.707c-.218 2.526 1.002 4.538 3.92 4.538 2.265 0 4.094-1.37 4.094-3.767 0-1.284-.741-2.226-2.352-2.825l-3.789-1.413c-3.615-1.327-6.054-3.638-6.054-7.662 0-5.65 4.399-9.717 10.845-9.717 5.879 0 10.017 3.553 9.625 9.674h-6.664c.174-2.055-.914-3.895-3.31-3.895-1.785 0-3.179 1.027-3.179 2.953 0 1.37 1.089 2.098 2.613 2.697l3.615 1.413c3.92 1.54 6.097 3.638 6.097 7.362ZM144.629 35.728l-14.425 20.066L128.155 66h-7.408l2.222-11.028-6.711-19.245h7.583l4.14 12.888 8.324-12.888zM92.541 66h-7.314l6.023-30.272h13.166c5.981 0 10.068 4.67 10.068 10.206 0 5.405-3.7 9.687-8.777 11.33L112.204 66h-9.423l-5.636-8h-3.012zm10.455-23.959h-5.722l-1.893 9.644h5.851c3.098 0 5.938-2.119 5.938-5.362 0-2.336-1.85-4.282-4.174-4.282ZM68.457 34.835c8.352 0 15 5.48 15 13.783 0 8.99-7.032 17.38-17.003 17.38-8.267 0-14.915-5.565-14.915-13.955 0-9.332 6.946-17.208 16.918-17.208Zm-9.674 16.823c0 4.965 3.111 8.09 7.97 8.09 5.88 0 9.46-5.265 9.46-10.702 0-4.366-3.154-7.961-8.012-7.961-5.71 0-9.418 5.008-9.418 10.573ZM26.71 42.128l1.227-6.4h23.599l-1.227 6.4h-8.204L37.453 66h-7.19l4.652-23.872zM23.198 55.04c0 6.42-5.27 10.958-11.759 10.958-6.576 0-10.844-3.553-10.409-10.317h6.707c-.217 2.526 1.002 4.538 3.92 4.538 2.265 0 4.094-1.37 4.094-3.767 0-1.284-.74-2.226-2.352-2.825L9.61 52.214c-3.615-1.327-6.054-3.638-6.054-7.662 0-5.65 4.4-9.717 10.845-9.717 5.88 0 10.017 3.553 9.625 9.674h-6.664c.175-2.055-.914-3.895-3.31-3.895-1.785 0-3.179 1.027-3.179 2.953 0 1.37 1.089 2.098 2.613 2.697l3.615 1.413c3.92 1.54 6.097 3.638 6.097 7.362ZM148.172 7.4l1.226-6.4h23.599l-1.227 6.4h-8.204l-4.652 23.872h-7.19L156.376 7.4zM127.181 31.272h-7.376L125.836 1h20.567l-1.259 6.4h-13.19l-1.172 5.839h12.8l-1.301 6.4h-12.8zM113.801 31.272h-7.293L112.514 1h7.293zM95.016 1 90.27 24.872h12.689l-1.252 6.4H81.68L87.68 1z"
    ></path>
  </svg>
)

const M04TeaserMagazine = ({ magazineHeading, anchorLinkId }) => {
  const translations = useTranslations()
  const latestFeaturedArticle = useLatestFeaturedArticle()
  const pathPrefix = usePathPrefix()

  const authorPhotographer = latestFeaturedArticle.articleBlocks.find(
    block =>
      block.__typename ===
      'CraftContent_articleBlocks_magazineHeader_BlockType',
  )?.authorPhotographer
  const readTimeMinutes = useReadTime(latestFeaturedArticle)

  const ref1 = useRef(null)
  const ref2 = useRef(null)
  const offsetTop = 120
  const offsetBottom = 80
  const { yRef1, recalculateParallax } = useParallaxCalculation({
    ref1,
    ref2,
    offsetTop,
    offsetBottom,
  })

  return (
    <section
      className="relative my-20 text-black bg-white md:my-28 pb-14"
      id={anchorLinkId}
    >
      <div className="absolute left-0 right-0 top-0 hidden md:block h-[14px] md:h-[124px] xl:h-[175px] bg-gray-1 z-0" />
      <div className="container z-10">
        <div className="row-type-a">
          <div
            className="col-1-type-a h-full relative -top-3.5 md:top-0 mb-10 md:mb-0 flex items-center justify-end md:justify-center"
            ref={ref1}
          >
            <motion.div
              style={{ y: yRef1 }}
              className="inline-block mt-20 xl:leading-[56px] z-10 md:mt-[124px] xl:mt-[175px] link-wrapper"
              // dangerouslySetInnerHTML={{ __html: nl2br(magazineHeading) }}
            >
              <MagazineHeading className="w-auto h-16 xl:h-[108px]" />
            </motion.div>
          </div>
          <div className="z-10 col-2-type-a" ref={ref2}>
            <div className="column-indent">
              <img
                src={latestFeaturedArticle.featureImage[0]?.url}
                alt={latestFeaturedArticle.title}
                onLoad={recalculateParallax}
                className="w-full h-auto rounded mb-9 shadow-tpmp lg:shadow-dp"
              />
            </div>
            <h2 className="mb-4 u-h1-mag lg:mb-6 xl:mb-5">
              <Link
                to={`${pathPrefix}/lift-storys/${latestFeaturedArticle.slug}`}
                className="underline transition-colors hover:text-red-as"
              >
                {latestFeaturedArticle.title}
              </Link>
            </h2>
            <div className="u-p-small column-indent">
              {authorPhotographer}
              <br />
              {readTimeMinutes} {translations.minsReadingTime}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default M04TeaserMagazine
